/* eslint-disable max-len */
import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { deleteGeoPhotoInput, DELETE_GEO_PHOTO, getGeoPhotoOutput, getGeoPhotosInput, GET_GEO_PHOTO } from '../../../api';
import { DeleteConfirmation, globalPageSizeOptions, PaginationBar } from '../../../components';
import { geoPhoto } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import { capitalizeFirstLetterEveryword } from '../../../shared/stringFunctions';
import { TXT_LIST_PHOTO, TXT_SEARCH_BY_NAME } from '../../../translations/translationConstants';
import { ManagePhotosTable } from './ManagePhotoTable';

interface ManagePhotosProps {
  boreholeId?: string;
}

export const ManagePhotos = () => {
  const { t } = useTranslation();
  const params = useParams<ManagePhotosProps>();
  const { isSignedIn } = useAuthServices();

  const [selectedGeoPhoto, setSelectedGeoPhoto] = useState<geoPhoto | undefined>(undefined);

  const [getGeoPhotos, { loading, data, error, refetch }] = useLazyQuery<getGeoPhotoOutput>(GET_GEO_PHOTO);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);
  const [openEditor, setOpenEditor] = useState(false);
  const [txtSearch, setTxtSearch] = useState('');

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const fetchGeoPhotos = () => {
    const variables: getGeoPhotosInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize,
      filter: {
        boreholeId: {
          eq: params.boreholeId
        }
      }
    };
    getGeoPhotos({
      variables
    });
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetchGeoPhotos();
  }, [data, pageNumber]);

  const onOpenDeleteConfirmation = (geoPhoto: geoPhoto) => {
    setSelectedGeoPhoto(geoPhoto);
    setOpenDeleteConfirmation(true);
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteGeoPhoto, deleteGeoPhotoOperation] = useMutation<boolean>(DELETE_GEO_PHOTO, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.geoPhotos.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteGeoPhotoInput = {
      id: selectedGeoPhoto!.id!
    };
    deleteGeoPhoto({
      variables: {
        input
      }
    });
  };

  const onOpenEditGeoPhotoEditor = (geoPhoto: geoPhoto) => {
    setSelectedGeoPhoto(geoPhoto);
    setOpenEditor(true);
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_LIST_PHOTO))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            { alert }
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteGeoPhotoOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedGeoPhoto?.code || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManagePhotosTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={onOpenEditGeoPhotoEditor}
                    onDelete={onOpenDeleteConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.geoPhotos.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
